import { Button, IconButton, InputDay } from '@vrplatform/components';
import {
  type RequestBody,
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
} from '@vrplatform/data';
import { day } from '@vrplatform/utils';
import { Box, Group, Popover, Stack, Text, rem } from '@mantine/core';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { Reservation } from '../_queries/useLedgerReservationDetailDrawerQuery';
import {
  formatReservationDates,
  getReservationNights,
} from './ReservationDatesPopover.utils';

type Props = {
  reservationId: string;
  checkIn?: Reservation['checkIn'];
  checkOut?: Reservation['checkOut'];
  lock?: Reservation['lock'];
  showNightsInline?: boolean;
};

type FormInputs = {
  checkIn: string | null;
  checkOut: string | null;
};

const toYyyyMmDd = (value: Date | string | null) => {
  if (!value) return null;

  const parsed = day(value);

  if (!parsed.isValid()) return null;

  return parsed.yyyymmdd();
};

export const ReservationDatesPopover = ({
  reservationId,
  checkIn,
  checkOut,
  lock,
  showNightsInline = true,
}: Props) => {
  const [opened, setOpened] = useState(false);
  const isLocked = lock?.status === 'locked';
  const methods = useForm<FormInputs>({
    values: {
      checkIn: checkIn ?? null,
      checkOut: checkOut ?? null,
    },
  });

  const checkInValue = methods.watch('checkIn');
  const checkOutValue = methods.watch('checkOut');

  const { mutateAsync, isPending } = useApiMutation(
    'put',
    '/reservations/{id}'
  );
  const invalidate = useInvalidateApiQueries(['reservations']);

  const closePopover = () => {
    methods.reset({
      checkIn: checkIn ?? null,
      checkOut: checkOut ?? null,
    });
    setOpened(false);
  };

  const lockedTooltip = isLocked ? lock.message : undefined;

  const datesText = useMemo(
    () => formatReservationDates(checkIn, checkOut),
    [checkIn, checkOut]
  );
  const savedNights = useMemo(
    () => getReservationNights(checkIn, checkOut),
    [checkIn, checkOut]
  );
  const currentNights = useMemo(
    () => getReservationNights(checkInValue, checkOutValue),
    [checkInValue, checkOutValue]
  );

  const hasValidDateRange = currentNights !== null;
  const showDateRangeError =
    !!checkInValue && !!checkOutValue && !hasValidDateRange;

  const saveDates = async () => {
    if (!hasValidDateRange) return;

    const body = {
      checkIn: checkInValue,
      checkOut: checkOutValue,
    } satisfies Pick<
      RequestBody<'put:/reservations/{id}'>,
      'checkIn' | 'checkOut'
    >;

    await mutateAsync({
      params: {
        path: {
          id: reservationId,
        },
      },
      body,
    })
      .then(() => {
        invalidate();
        closePopover();
      })
      .catch((error) =>
        showApiErrorNotification({
          error,
          title: 'Failed to update reservation',
        })
      );
  };

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        width: '100%',
        justifyContent: 'space-between',
        gap: rem(8),
      }}
    >
      <Box
        component="span"
        sx={(theme) => ({
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: theme.fontSizes.sm,
        })}
      >
        {datesText || '-'}
        {showNightsInline && savedNights !== null && (
          <Text component="span" c="dimmed" size="sm">
            {` (${savedNights} ${savedNights === 1 ? 'day' : 'days'})`}
          </Text>
        )}
      </Box>

      <Popover
        opened={opened}
        onChange={(nextOpened) => {
          setOpened(nextOpened);
          if (!nextOpened) {
            methods.reset({
              checkIn: checkIn ?? null,
              checkOut: checkOut ?? null,
            });
          }
        }}
        position="bottom-end"
        offset={10}
        withinPortal
        shadow="lg"
        withArrow
        arrowOffset={10}
        arrowSize={12}
      >
        <Popover.Target>
          <IconButton
            icon="Edit3Icon"
            iconSize={16}
            aria-label="Edit reservation dates"
            tooltip={lockedTooltip}
            tooltipMaxWidth={400}
            disabled={isLocked}
            onClick={() => setOpened(true)}
          />
        </Popover.Target>

        <Popover.Dropdown p="md" onClick={(event) => event.stopPropagation()}>
          <Stack gap="xs">
            <Group
              justify="space-between"
              align="center"
              wrap="nowrap"
              gap="xl"
            >
              <Text fw={500} size="sm">
                Check-in date
              </Text>
              <Box w={120}>
                <Controller
                  control={methods.control}
                  name="checkIn"
                  render={({ field }) => (
                    <InputDay
                      aria-label="Check-in date"
                      value={field.value ? day(field.value).toDate() : null}
                      onChange={(value) => field.onChange(toYyyyMmDd(value))}
                      maxDate={
                        checkOutValue
                          ? day(checkOutValue).subtract(1, 'day').toDate()
                          : undefined
                      }
                      popoverProps={{
                        withinPortal: false,
                      }}
                      valueFormat="MMM DD, YYYY"
                    />
                  )}
                />
              </Box>
            </Group>

            <Group
              justify="space-between"
              align="center"
              wrap="nowrap"
              gap="xl"
            >
              <Text fw={500} size="sm">
                Checkout date
              </Text>
              <Box w={120}>
                <Controller
                  control={methods.control}
                  name="checkOut"
                  render={({ field }) => (
                    <InputDay
                      aria-label="Checkout date"
                      value={field.value ? day(field.value).toDate() : null}
                      onChange={(value) => field.onChange(toYyyyMmDd(value))}
                      minDate={
                        checkInValue
                          ? day(checkInValue).add(1, 'day').toDate()
                          : undefined
                      }
                      error={showDateRangeError}
                      popoverProps={{
                        withinPortal: false,
                      }}
                      valueFormat="MMM DD, YYYY"
                    />
                  )}
                />
              </Box>
            </Group>

            <Group
              justify="space-between"
              align="center"
              wrap="nowrap"
              mt={6}
              mb={6}
            >
              <Text fw={500} size="sm">
                Total nights
              </Text>

              <Box w={120}>
                <Text c="dimmed" size="sm">
                  {currentNights !== null ? `${currentNights} nights` : '-'}
                </Text>
              </Box>
            </Group>

            {showDateRangeError && (
              <Text c="red" size="xs">
                Checkout date has to be after check-in date.
              </Text>
            )}

            <Group justify="flex-end" mt={4}>
              <Button onClick={closePopover} disabled={isPending}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={saveDates}
                loading={isPending}
                disabled={!hasValidDateRange}
              >
                Save dates
              </Button>
            </Group>
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </Box>
  );
};
